import { fitApi as campaignApi } from '@mehir/api/campaign-api';
import { contractApi } from '@mehir/api/contract-api';
import { blogApi } from '@mehir/api/blog-api';
import { fitApi } from '@mehir/api/fit-api';
import { finance as financeApi } from '@mehir/api/finance-api';
import { authApi } from '@mehir/api/auth-api';
import { backofficeApi } from '@mehir/api/backoffice-api';

/**
 * Cache tags for the endpoints only the backoffice calls.
 *
 * The tag files in libs/api were written for the mobile app and cover its
 * surface; the backoffice list/create/update/delete operations carried no tags
 * at all, which is why nearly every screen patched over it with a manual
 * refetch(). These live here rather than in libs/api so the mobile bundle is
 * untouched.
 *
 * Each service is enhanced through its own typed api object — enhancing the
 * bare `mehirApi` would accept any endpoint name, so a typo would compile and
 * then silently do nothing.
 *
 * (campaign-api exports its api as `fitApi` because of a generator bug; the
 * existing tag file renames it the same way.)
 */
export const campaignBackofficeTags = campaignApi.enhanceEndpoints({
  endpoints: {
    getAllCampaigns: { providesTags: ['Campaigns'] },
    getCampaignById: { providesTags: ['Campaigns'] },
    createCampaign: { invalidatesTags: ['Campaigns'] },
    updateCampaign: { invalidatesTags: ['Campaigns'] },
    deleteCampaign: { invalidatesTags: ['Campaigns'] },
    // Codes and brands have no tag type of their own; keying them to Campaigns
    // over-invalidates slightly and never goes stale.
    getAllCodes: { providesTags: ['Campaigns'] },
    listAllBrands: { providesTags: ['Campaigns'] },
    getAllCategoriesBackoffice: { providesTags: ['CampaignCategories'] },
  },
});

export const contractBackofficeTags = contractApi.enhanceEndpoints({
  endpoints: {
    getAllContracts: { providesTags: ['Contract'] },
    getContractById: { providesTags: ['Contract'] },
    getContractByUserIdBackoffice: { providesTags: ['Contract'] },
    getSavingPlan: { providesTags: ['PayPlan'] },
  },
});

export const blogBackofficeTags = blogApi.enhanceEndpoints({
  endpoints: {
    getAllBlogs: { providesTags: ['Blogs'] },
    getBlogById: { providesTags: ['Blogs'] },
    createBlogAsync: { invalidatesTags: ['Blogs'] },
    updateBlogAsync: { invalidatesTags: ['Blogs'] },
    deleteBlogAsync: { invalidatesTags: ['Blogs'] },
    reorderBlog: { invalidatesTags: ['Blogs'] },

    getAllAnnouncements: { providesTags: ['Announcements'] },
    getAnnouncementById: { providesTags: ['Announcements'] },
    createAnnouncementAsync: { invalidatesTags: ['Announcements'] },
    updateAnnouncementAsync: { invalidatesTags: ['Announcements'] },
    deleteAnnouncementAsync: { invalidatesTags: ['Announcements'] },

    getAllFaqs: { providesTags: ['Faqs'] },
    createFaqAsync: { invalidatesTags: ['Faqs'] },
    updateFaqAsync: { invalidatesTags: ['Faqs'] },
    deleteFaqAsync: { invalidatesTags: ['Faqs'] },
    getAllFaqCategories: { providesTags: ['FaqCategories'] },
    createFaqCategoryAsync: { invalidatesTags: ['FaqCategories', 'Faqs'] },
    updateFaqCategoryAsync: { invalidatesTags: ['FaqCategories', 'Faqs'] },
    deleteFaqCategoryAsync: { invalidatesTags: ['FaqCategories', 'Faqs'] },

    getAllAppContracts: { providesTags: ['AppContracts'] },
    createAppContract: { invalidatesTags: ['AppContracts'] },
    updateAppContract: { invalidatesTags: ['AppContracts'] },
    deleteAppContract: { invalidatesTags: ['AppContracts'] },

    getAllContactInfos: { providesTags: ['ContactInfos'] },
    createContactInfo: { invalidatesTags: ['ContactInfos'] },
    updateContactInfo: { invalidatesTags: ['ContactInfos'] },
    deleteContactInfo: { invalidatesTags: ['ContactInfos'] },
    reorderContactInfo: { invalidatesTags: ['ContactInfos'] },
  },
});

export const fitBackofficeTags = fitApi.enhanceEndpoints({
  endpoints: {
    getAllVideosBackoffice: { providesTags: ['FitVideos'] },
    getVideoById: { providesTags: ['FitVideos'] },
    createVideo: { invalidatesTags: ['FitVideos'] },
    updateVideo: { invalidatesTags: ['FitVideos'] },
    deleteVideo: { invalidatesTags: ['FitVideos'] },
    getAllExercises: { providesTags: ['Exercises'] },
    getAllTargets: { providesTags: ['Targets'] },
    getAllExerciseTypes: { providesTags: ['ExerciseTypes'] },
  },
});

export const financeBackofficeTags = financeApi.enhanceEndpoints({
  endpoints: {
    getAllCurrenciesBackoffice: { providesTags: ['Currencies'] },
    getAllMetalsBackoffice: { providesTags: ['Metals'] },
    getAllFundsBackoffice: { providesTags: ['Funds'] },
  },
});

export const authBackofficeTags = authApi.enhanceEndpoints({
  endpoints: {
    getAllMehirAppUsers: { providesTags: ['User'] },
    getMehirAppUserById: { providesTags: ['User'] },
    getAllReferralsAsync: { providesTags: ['InvitedUsers'] },
    getReferralByIdAsync: { providesTags: ['InvitedUsers'] },
    getReferralStatsAsync: { providesTags: ['InvitedUsers'] },
    getAllUserEarningsAsync: { providesTags: ['InvitedUsers'] },
    getUserEarningsByUserIdAsync: { providesTags: ['InvitedUsers'] },
    getEarningsBreakdownAsync: { providesTags: ['InvitedUsers'] },
    getAllCommunicationPermissionsAsync: { providesTags: ['CommunicationPermissions'] },
  },
});

export const systemUserTags = backofficeApi.enhanceEndpoints({
  endpoints: {
    getCurrentUserAsync: { providesTags: ['User'] },
    changePasswordAsync: { invalidatesTags: ['User'] },
    getAllUsersAsync: { providesTags: ['User'] },
    createSystemUserAsync: { invalidatesTags: ['User'] },
    updateUserAsync: { invalidatesTags: ['User'] },
    updateUserStatusAsync: { invalidatesTags: ['User'] },
    deleteUserAsync: { invalidatesTags: ['User'] },
  },
});
